import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { SeoManager } from './components/SeoManager';
import { Toaster } from 'sonner';

// Contexts (keep eager - needed immediately)
import { LanguageProvider } from './contexts/LanguageContext';
import { AuthProvider } from './contexts/AuthContext';

// Layout components (keep eager - needed for initial render)
import { Header, Footer } from './components/Layout';
import { ScrollToTop } from './components/ScrollToTop';
import { CookieBanner } from './components/CookieBanner';
import { PWAInstallPrompt } from './components/PWAInstallPrompt';

// Auth Routes (keep eager - critical path)
import { AuthCallback, ProtectedRoute } from './components/AuthRoutes';

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-950">
    <div className="flex flex-col items-center gap-4">
      <div className="w-12 h-12 border-4 border-[#0AFFD6]/30 border-t-[#0AFFD6] rounded-full animate-spin" />
      <p className="text-slate-400 text-sm">Cargando...</p>
    </div>
  </div>
);

// Lazy load all pages
const HomePage = lazy(() => import('./pages/HomePage').then(m => ({ default: m.HomePage })));
const Home1Page = lazy(() => import('./pages/Home1Page').then(m => ({ default: m.Home1Page })));
const InsomniaPage = lazy(() => import('./pages/InsomniaPage').then(m => ({ default: m.InsomniaPage })));
const AnxietyPage = lazy(() => import('./pages/AnxietyPage').then(m => ({ default: m.AnxietyPage })));
const TechnologyPage = lazy(() => import('./pages/TechnologyPage').then(m => ({ default: m.TechnologyPage })));
const PricingPage = lazy(() => import('./pages/PricingPage').then(m => ({ default: m.PricingPage })));
const LoginPage = lazy(() => import('./pages/AuthPages').then(m => ({ default: m.LoginPage })));
const RegisterPage = lazy(() => import('./pages/AuthPages').then(m => ({ default: m.RegisterPage })));
const ForgotPasswordPage = lazy(() => import('./pages/PasswordResetPages').then(m => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('./pages/PasswordResetPages').then(m => ({ default: m.ResetPasswordPage })));
const DashboardPage = lazy(() => import('./pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const ProfilePage = lazy(() => import('./pages/ProfilePage').then(m => ({ default: m.ProfilePage })));
const SubscriptionSuccessPage = lazy(() => import('./pages/ProfilePage').then(m => ({ default: m.SubscriptionSuccessPage })));
const QuizPage = lazy(() => import('./pages/QuizPage').then(m => ({ default: m.QuizPage })));
const StatsPage = lazy(() => import('./pages/StatsPage').then(m => ({ default: m.StatsPage })));
const AdminPage = lazy(() => import('./pages/AdminPage').then(m => ({ default: m.AdminPage })));
const TermsPage = lazy(() => import('./pages/TermsPage').then(m => ({ default: m.TermsPage })));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage').then(m => ({ default: m.PrivacyPage })));
const CookiesPage = lazy(() => import('./pages/CookiesPage').then(m => ({ default: m.CookiesPage })));
const BlogPage = lazy(() => import('./pages/BlogPage').then(m => ({ default: m.BlogPage })));
const BlogPostPage = lazy(() => import('./pages/BlogPage').then(m => ({ default: m.BlogPostPage })));
const PlaylistPage = lazy(() => import('./pages/PlaylistPage').then(m => ({ default: m.PlaylistPage })));
const PlaylistsPage = lazy(() => import('./pages/PlaylistsPage').then(m => ({ default: m.PlaylistsPage })));
const PlaylistDetailPage = lazy(() => import('./pages/PlaylistDetailPage').then(m => ({ default: m.PlaylistDetailPage })));
const SmartPlaylistPage = lazy(() => import('./pages/SmartPlaylistPage'));
const TerapiasPage = lazy(() => import('./pages/TerapiasPage'));
const PanelPage = lazy(() => import('./pages/PanelPage'));

import './App.css';

const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      <ScrollToTop />
      <Header />
      <main className="min-h-screen">
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/home1" element={<Home1Page />} />
            <Route path="/insomnia" element={<InsomniaPage />} />
            <Route path="/anxiety" element={<AnxietyPage />} />
            <Route path="/terapias" element={<TerapiasPage />} />
            <Route path="/mi-programa" element={<SmartPlaylistPage publicMode />} />
            <Route path="/technology" element={<TechnologyPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/quiz" element={<QuizPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            
            {/* Legal Pages */}
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/cookies" element={<CookiesPage />} />
            
            {/* Blog */}
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            
            {/* Auth Callback */}
            <Route path="/auth/callback" element={<AuthCallback />} />
            
            {/* Subscription Success */}
            <Route path="/subscription/success" element={
              <ProtectedRoute>
                <SubscriptionSuccessPage />
              </ProtectedRoute>
            } />

            {/* Protected Routes */}
            <Route path="/panel" element={
              <ProtectedRoute>
                <PanelPage />
              </ProtectedRoute>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            } />
            <Route path="/playlist" element={
              <ProtectedRoute>
                <PlaylistPage />
              </ProtectedRoute>
            } />
            <Route path="/playlists" element={
              <ProtectedRoute>
                <PlaylistsPage />
              </ProtectedRoute>
            } />
            <Route path="/playlists/smart/create" element={
              <ProtectedRoute>
                <SmartPlaylistPage />
              </ProtectedRoute>
            } />
            <Route path="/playlists/:playlistId" element={
              <ProtectedRoute>
                <PlaylistDetailPage />
              </ProtectedRoute>
            } />
            <Route path="/profile" element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            } />
            <Route path="/stats" element={
              <ProtectedRoute>
                <StatsPage />
              </ProtectedRoute>
            } />
            <Route path="/admin" element={
              <ProtectedRoute>
                <AdminPage />
              </ProtectedRoute>
            } />
          </Routes>
        </Suspense>
      </main>
      <SeoManager />
      <Footer />
      <CookieBanner />
    </>
  );
};

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <div className="App min-h-screen bg-slate-950">
            <AppRouter />
            <Toaster
              position="top-right"
              toastOptions={{
                style: {
                  background: '#0f172a',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  color: '#f8fafc',
                },
              }}
            />
            <PWAInstallPrompt />
          </div>
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
